import { useNavigate } from "react-router-dom";
import "./ApprovalModal.css";
import Modal from "@mui/material/Modal";
import InfoIconImgSrc from "../../../assets/image/png/info.png";
import TickconImgSrc from "../../../assets/image/png/tick.png";
import { resetClaimsState } from "../../../stateManagement/actions/claims";
import { useDispatch } from "react-redux";
import { postClaimApprovalDetails, updateClaimedStock } from "../../../services/claimService";
import { getLastNPathSegments} from "../utils/utils";
import { useState } from "react";
import { setOpenClaimsRow } from "../../../stateManagement/actions/claims";
import { logAnalyticsEvent } from "../../../../src/firebase";

interface ClaimActionConfirmationModalProps {
  approvalModalOpen: boolean;
  setApprovalModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  approvalModalState: number;
  setApprovalModalState: React.Dispatch<React.SetStateAction<number>>;
  getPostBody: (rows: any[]) => any;
  approvalText?: React.ReactNode;
  approvalMessage?: React.ReactNode;
  isOpenClaimsPage?: boolean;
  handleSubmit?: () => Promise<any[]>;
  approvalConfig?: any;
  postBodyRef: React.MutableRefObject<any>;
  errorMessage: string | null;
  setErrorMessage: (message: string | null) => void;
  claimType: string;
  claimSubtype: string;
  distributorId: string;
}
  

const ClaimActionConfirmationModal: React.FC<ClaimActionConfirmationModalProps> = ({
  approvalModalOpen,
  setApprovalModalOpen,
  approvalModalState,
  setApprovalModalState,
  getPostBody,
  approvalText,
  approvalMessage,
  isOpenClaimsPage = false,
  handleSubmit,
  approvalConfig,
  postBodyRef,
  errorMessage,
  setErrorMessage,
  claimType,
  claimSubtype,
  distributorId
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<React.ReactNode>(approvalMessage ?? "");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const path = getLastNPathSegments(window.location.href, 2);
  const lastSegment = path[0];
  const handleClose = () => {
    setApprovalModalState(1);
    setApprovalModalOpen(false);
    if (approvalModalState === 2) {
      navigate("/dashboard/claims/status");
      dispatch(resetClaimsState());
    }
  };

  const handleModalSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    let updatedRows = null;
    try {
      if (handleSubmit) {
        updatedRows = await handleSubmit();
        if (updatedRows) {
          const { postBody, requestId, claimedStockBody, newStatus } = getPostBody ? getPostBody(updatedRows) : { postBody: {}, requestId: null, claimedStockBody: null, newStatus: null };

          if (claimedStockBody && claimedStockBody.skuDetails?.length > 0) {
            await updateClaimedStock(claimedStockBody);
          }
          await postClaimApprovalDetails(postBody, requestId);
          dispatch(setOpenClaimsRow(updatedRows));
          setApprovalModalState(2);
          
          logAnalyticsEvent("claim_status_updated", {
            distributor_id: distributorId,
            claim_id: postBody.referenceNumber,
            claim_type: claimType,
            claim_subtype: claimSubtype,
            new_status: newStatus
          });
        }
      }
    } catch (error: any) {
      console.error("Error occurred during submission:", error);
      dispatch(setOpenClaimsRow(updatedRows));
      setApprovalModalState(3);
      setErrorMessage(
        error.response?.data?.exception || 
        error.response?.data?.message || 
        "An unexpected error occurred. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNavigate = () => {
    setApprovalModalState(1);
    setErrorMessage(null);
    setApprovalModalOpen(false);
    
    if (isOpenClaimsPage) {
      const isHighestLevel = approvalConfig?.flow && approvalConfig.currentLevel !== undefined
        ? approvalConfig.currentLevel === approvalConfig.flow.length - 1
        : false;
      
        const allRejected = postBodyRef.current?.status === 'REJECTED';
      
      if (isHighestLevel || allRejected) {
        navigate("/dashboard/claimsStatus/closed");
      } else {
        navigate(-1); 
      }
    } else if (lastSegment === "summary") {
      navigate("/dashboard/claims/status");
      dispatch(resetClaimsState());
    }
  };

  return (
    <Modal
      open={approvalModalOpen}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <div className="claim-confirmation-approval-modal-container">
        <div className="info-icon">
          <img
            src={errorMessage || approvalModalState === 1 || approvalModalState === 3 ? InfoIconImgSrc : TickconImgSrc}
            alt="icon"
          />
        </div>
        <p className="warning-message" style={{ width: approvalModalState === 1 ? "85%" : "78%" }}>
          {errorMessage ? errorMessage : approvalModalState === 2 ? successMessage : approvalText}
        </p>
        <div className="yes-no-button-container">
          {approvalModalState === 1 && !errorMessage && (
            <button
              className="button-common-styles button-no"
              onClick={() => setApprovalModalOpen(false)}
            >
              <span className="no-button-text">No</span>
            </button>
          )}
          <button
            className="button-common-styles button-yes"
            style={{ width: approvalModalState === 1 ? "" : "137px" }}
            onClick={async () => {
              if (approvalModalState === 3 || errorMessage) {
                setApprovalModalState(1);
                setErrorMessage(null);
                setApprovalModalOpen(false);
              } else if (approvalModalState === 1) {
                await handleModalSubmit();
              } else {
                handleNavigate();
              }
            }}
            disabled={isSubmitting}
          >
            <span className="yes-button-text">
              {errorMessage ? "Continue" : approvalModalState === 1 ? "Yes" : "Continue"}
            </span>
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ClaimActionConfirmationModal;